import React, { useCallback, useEffect } from 'react';
import { FunctionComponent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { copyTask } from '../../../../../app/page/page-storage/boardLists/boardLists.actions';
import { updateTaskSimple } from '../../../../../app/page/page-storage/tasks-simple/tasks-simple.actions';
import { IActionsForm } from '../../../../../app/resource/task/task.types';
import { saveTaskOrder } from '../../../../../app/page/page-storage/tasks-simple/tasks-simple.actions';
import { useRequiredValidation } from '../../../../../utils/form/validation.utils';
import { useSelect, useSelectWithParams } from '../../../../../utils/selector.utils';
import { IActionsTaskFormProps } from './types';
import { initialBoardsForMeeting } from '../../../../../app/page/home/home.actions';
import { meetingSimpleSelectors } from '../../../../../app/resource/meeting-simple/meeting-simple.selectors';
import { authSelectors } from '../../../../../app/auth/auth.selectors';
import { userSelectors } from '../../../../../app/resource/user/user.selectors';
import { isMemberPartOfMeeting } from '../../../../../utils/user.utils';
import { ITaskSimple } from '../../../../../app/resource/task-simple/task-simple.types';
import { openDialog, closeDialog } from '../../../../../app/dialog/dialog.actions';
import { SelectMeetingInput } from '../../inputs/SelectMeetingInput';
import { SelectBoardInput } from '../../inputs/SelectBoardInput';
import { SelectBoardListInput } from '../../inputs/SelectBoardListInput';
import { IMeetingSimple } from '../../../../../app/resource/meeting-simple/meeting-simple.types';

export const ActionsTaskForm: FunctionComponent<IActionsTaskFormProps> = ({ task, move, onClick }) => {
  const dispatch = useDispatch();
  const requiredValidation = useRequiredValidation();
  const formProps = useForm();
  const { setValue } = formProps;

  const loggedUserId = useSelect(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');

  const currentMeeting = task.meetingIds[0];
  const currentBoard = task.boardId;
  const currentBoardList = task.taskTypeId;

  const isActionItem = task.showInTasks === '0' ? false : true;
  const hasUserAssigned = task.members.length > 0;

  const resetDependentsFieldsFor = useCallback(
    (changedField: string) => {
      if (changedField === 'meeting') {
        setValue('board', '');
        setValue('boardList', '');
      }
      if (changedField === 'board') {
        setValue('boardList', '');
      }
    },
    [setValue],
  );

  const meetings = useSelect(meetingSimpleSelectors.selectResourceList);

  let filteredMeetings = meetings;
  if (loggedUser) {
    filteredMeetings = meetings.filter((meeting) => isMemberPartOfMeeting(loggedUser, meeting));
  }

  const focusMeetingTuple = formProps.watch('meeting');
  useEffect(() => {
    if (focusMeetingTuple) {
      dispatch(initialBoardsForMeeting(focusMeetingTuple.value));
    }
  }, [dispatch, focusMeetingTuple]);

  const handleMove = useCallback(
    (taskId, taskTypeId, updatedTask) => {
      dispatch(saveTaskOrder([taskId], taskTypeId));
      dispatch(updateTaskSimple(task.id, updatedTask));
    },
    [dispatch, task.id],
  );

  const check = async (data: IActionsForm, cb: (arg0: boolean) => void) => {
    const taskMembersIds = task.members;
    const selectedMeeting = filteredMeetings.find((meeting) => meeting.id === data.meeting.value) as IMeetingSimple;
    const meetingMembersIds = Array.from(
      new Set([...selectedMeeting.shareWithIds, ...selectedMeeting.administratorIds]),
    );

    for (const taskMemberId of taskMembersIds) {
      if (!meetingMembersIds.includes(taskMemberId)) {
        const confirmed = await new Promise((resolve) => {
          dispatch(
            openDialog('confirmation', {
              title: 'Meeting members',
              message:
                'This card has members assigned that are not present in the selected meeting and will be unassigned. Do you want to continue anyway?',
              onConfirm: () => resolve(true),
              onDeny: () => resolve(false),
              styles: { zIndex: '999999999', position: 'fixed' },
            }),
          );
        });

        if (!confirmed) {
          cb(false);
          return;
        }
        // if a task member is not present in the destination meeting, we unassign it
        const membersPresentInMeeting = taskMembersIds.filter((memberId) => meetingMembersIds.includes(memberId));
        if (membersPresentInMeeting.length !== meetingMembersIds.length) {
          task = { ...task, members: membersPresentInMeeting };
        }
      }
    }
    cb(true);
  };

  const onSubmit = (data: IActionsForm) => {
    check(data, function (ok: boolean) {
      if (ok) {
        // we do not use meeting field in data because the backend is in charge of moving
        // the task to new meetings if necessary using the task type info
        onClick(true);
        const newData = { id: task.id, taskTypeId: data.boardList.value };
        const updatedTask = {
          ...task,
          meetingIds: [data.meeting.value],
          taskTypeId: data.boardList.value && data.boardList.value !== '' ? data.boardList.value : null,
          boardId: data.board.value !== '' ? data.board.value : null,
          status: '0',
        } as ITaskSimple;

        move ? handleMove(task.id, data.boardList.value, updatedTask) : dispatch(copyTask(newData));
        dispatch(closeDialog('confirmation'));
      } else {
        dispatch(closeDialog('confirmation'));
      }
    });
  };

  return (
    <FormProvider {...formProps}>
      <div>
        <form
          id="actions-task-form"
          className="topic-form"
          onSubmit={formProps.handleSubmit((formData) => onSubmit(formData as IActionsForm))}
          method="post"
        >
          <div className="field-container">
            <SelectMeetingInput
              validation={requiredValidation}
              onInputChange={resetDependentsFieldsFor}
              defaultMeetingId={currentMeeting}
              placeholder="Select meeting"
              isSingle
            />
          </div>
          <div className="field-container">
            <SelectBoardInput
              validation={requiredValidation}
              onInputChange={resetDependentsFieldsFor}
              defaultBoardId={currentBoard}
              allowEmpty={isActionItem && hasUserAssigned}
              isSingle
            />
          </div>
          <div className="field-container">
            <SelectBoardListInput
              validation={requiredValidation}
              onInputChange={resetDependentsFieldsFor}
              defaultBoardListId={currentBoardList}
              isSingle
            />
          </div>
          <button type="submit" className="btn btn-md white d-block w100 jcc" style={{ marginLeft: 0 }}>
            {move ? 'Move' : 'Copy'}
          </button>
        </form>
      </div>
    </FormProvider>
  );
};
